import type { AppDispatch } from '../index';
import {
  getGameById,
  getGamesSearch,
  type GamesSearchOrderBy,
} from '../../lib/api/games-api-client';
import { getPublicWebsiteConfig } from '../../lib/api/config';
import {
  buildGamesSearchKey,
  gameDetailFailed,
  gameDetailRequested,
  gameDetailSucceeded,
  gamesSearchFailed,
  gamesSearchRequested,
  gamesSearchSucceeded,
} from '../slices/games-slice';
import { hydrateGameAffiliateLinks } from './affiliate-thunks';

/**
 * Games thunks (task 6.5, public-website spec).
 *
 * Async actions wrapping `getGamesSearch` and `getGameById` from
 * the games API client per design §3.1 (Thunks layer): the only
 * place network side effects live in the Redux layer for the
 * games feature. Each thunk dispatches the matching slice actions
 * in lockstep — `requested` on entry, `succeeded` with the parsed
 * payload on success, `failed` with a string error on rejection —
 * so reducers stay pure and the cached lifecycle in
 * `state.games.searchPagesByKey` / `state.games.gameDetailById`
 * tracks the in-flight state the components in 6.7 / 6.8 / 6.9
 * read.
 *
 * `loadGameDetail` additionally implements the design §8.2
 * Game-page on-demand affiliate hydration: when the detail
 * response carries an empty `affiliateLinks` array, the thunk
 * dispatches `hydrateGameAffiliateLinks` to mint + fetch the
 * signed URL and merge the result back into the cached detail.
 *
 * Function-length budget: every export below stays under 20 lines
 * per Requirement 3.7.
 */

/** Input shape for `searchGames`. */
export interface SearchGamesInput {
  readonly q: string;
  readonly page?: number;
  readonly orderBy?: GamesSearchOrderBy;
}

/**
 * Fetch a page of games matching the search input and mirror the
 * call into the games slice.
 *
 * @param input The search input (`q` required, `page` / `orderBy` optional).
 * @returns A thunk that resolves once the matching slice action has
 *   been dispatched (`succeeded` on 2xx, `failed` on error).
 */
export function searchGames(input: SearchGamesInput) {
  return async (dispatch: AppDispatch): Promise<void> => {
    const key = buildGamesSearchKey(input);
    dispatch(gamesSearchRequested(input));
    try {
      const config = getPublicWebsiteConfig();
      const page = await getGamesSearch(input, {
        baseUrl: config.publicApiInvokeUrl,
        apiKey: config.publicApiKey,
      });
      dispatch(gamesSearchSucceeded({ key, page }));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(gamesSearchFailed({ key, error: message }));
    }
  };
}

/**
 * Fetch a single Game-page record and mirror the call into the
 * games slice. When the detail response carries an empty
 * `affiliateLinks` array, the thunk dispatches
 * `hydrateGameAffiliateLinks` to perform the design §8.2
 * signed-URL transport on demand.
 *
 * @param id The game id from the `:gameId` route param.
 * @returns A thunk that resolves once the detail slice action has
 *   been dispatched. The hydration thunk runs concurrently and is
 *   not awaited — the Game-page renders the body while the
 *   affiliate buttons are still loading.
 */
export function loadGameDetail(id: string) {
  return async (dispatch: AppDispatch): Promise<void> => {
    dispatch(gameDetailRequested(id));
    try {
      const config = getPublicWebsiteConfig();
      const response = await getGameById(
        { id },
        { baseUrl: config.publicApiInvokeUrl, apiKey: config.publicApiKey },
      );
      dispatch(gameDetailSucceeded({ id, detail: response.item }));
      if (response.item.affiliateLinks.length === 0) {
        await dispatch(
          hydrateGameAffiliateLinks({ id, gameTitle: response.item.title }),
        );
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(gameDetailFailed({ id, error: message }));
    }
  };
}
